/**
 * Canonical text styles for booking-page surfaces (apps/web).
 *
 * Every route under the booking subdomain (/, /book, /member, /manage,
 * /quote, /feedback) must use these instead of ad-hoc TEMPLATE_* scale +
 * opacity mixes, so all pages share one minimal black & white system:
 *
 * Current Micro Precision defaults:
 *
 *   standalone outer-card title
 *                18px / 700 / #101010   (standalone root token)
 *   page/location
 *                15px / 600 / #101010
 *   section      14px / 600 / #101010   (bookingSectionTitleStyle from package)
 *   body / name  14px / 500-600 / #101010
 *   meta         13px / 400 / #606060
 *
 * Colors come from the booking CSS variables so BookingThemeRoot overrides
 * still apply.
 */
import type { CSSProperties } from "react";

const text = "var(--openings-color-text, #111)";
const textSecondary = "var(--openings-color-text-secondary, #666)";
const fontBase = "var(--openings-booking-font-size, 14px)";
const fontSm = "var(--openings-booking-font-size-sm, 13px)";

/** Body-size emphasized text — service names, labels. */
export const bookingNameTextStyle: CSSProperties = {
  fontSize: fontBase,
  fontWeight: 600,
  lineHeight: 1.4,
  color: text,
};

/** Body-size regular text — prices, values. */
export const bookingBodyTextStyle: CSSProperties = {
  fontSize: fontBase,
  fontWeight: 500,
  lineHeight: 1.4,
  color: text,
};

/** Small secondary text — durations, descriptions, helper copy. */
export const bookingMetaTextStyle: CSSProperties = {
  fontSize: fontSm,
  color: textSecondary,
  lineHeight: 1.5,
};

/** Canonical prop bundle for ServiceItemRow across all booking surfaces. */
export const serviceItemRowStyles = {
  nameStyle: bookingNameTextStyle,
  priceStyle: bookingBodyTextStyle,
  durationStyle: {
    ...bookingMetaTextStyle,
    lineHeight: "var(--openings-booking-meta-line-height, 1.35)",
  },
  descriptionStyle: bookingMetaTextStyle,
} as const;
